import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Loader2, Sparkles } from 'lucide-react';
import { Card } from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  generatePrepBrief,
  getMatter,
  listMatterDocuments,
  type DocumentSummary,
  type PrepBriefResult,
} from '@/lib/api-client';
import type { MatterDetail } from '@/lib/types';

// Pre-hearing prep mode (P4 Section 3.2). Documents the model will use on the
// left; the generated brief on the right. The brief is also saved back to the
// matter's knowledge so it primes the live hearing.
export function Prep() {
  const { id = '' } = useParams<{ id: string }>();
  const [matter, setMatter] = useState<MatterDetail | null>(null);
  const [docs, setDocs] = useState<DocumentSummary[]>([]);
  const [result, setResult] = useState<PrepBriefResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void getMatter(id).then(setMatter).catch(() => setMatter(null));
    void listMatterDocuments(id).then(setDocs).catch(() => setDocs([]));
  }, [id]);

  async function run(): Promise<void> {
    setError(null);
    setBusy(true);
    setResult(null);
    try {
      setResult(await generatePrepBrief(id));
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Could not generate the brief. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  const readyDocs = docs.filter((d) => d.extractionStatus === 'READY');

  return (
    <div className="min-h-screen bg-stone-950 px-5 py-6 sm:px-6">
      <div className="mx-auto w-full max-w-5xl space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl italic text-stone-100">Pre-hearing prep</h1>
            {matter ? <p className="text-sm text-stone-400">{matter.name}</p> : null}
          </div>
          <Link
            to="/matters"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Matters
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[280px_1fr]">
          {/* Left: documents the model will use */}
          <aside className="space-y-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
              Documents in play ({readyDocs.length})
            </p>
            {docs.length === 0 ? (
              <Card variant="default" padding="md" className="text-sm text-stone-500">
                No documents uploaded yet.{' '}
                <Link to={`/admin/matters/${id}/documents`} className="text-amber-400 hover:underline">
                  Add some
                </Link>{' '}
                so the brief can cite them.
              </Card>
            ) : (
              <div className="space-y-1.5">
                {docs.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center gap-2 rounded-md border border-stone-800/60 bg-stone-900/50 px-3 py-2 text-sm"
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0 text-stone-500" />
                    <span className="min-w-0 flex-1 truncate text-stone-300">{d.filename}</span>
                    <span
                      className={
                        d.extractionStatus === 'READY'
                          ? 'text-[10px] uppercase text-emerald-500'
                          : 'text-[10px] uppercase text-stone-600'
                      }
                    >
                      {d.extractionStatus}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <p className="pt-2 text-[11px] leading-relaxed text-stone-600">
              The brief uses the case context, these documents, your matter knowledge, recent
              hearing transcripts, and a live Dropbox and Outlook search.
            </p>
          </aside>

          {/* Right: generate + rendered brief */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">The brief</p>
              <button
                type="button"
                onClick={() => void run()}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-stone-950 transition-colors hover:bg-amber-700 disabled:opacity-40"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {busy ? 'Generating' : result ? 'Regenerate brief' : 'Generate pre-hearing brief'}
              </button>
            </div>

            {error ? <p className="text-sm text-red-400">{error}</p> : null}

            {busy ? (
              <Card variant="default" padding="lg" className="text-sm text-stone-400">
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                Working through the matter, the documents, and a live search of your Dropbox and
                Outlook. This deep brief can take a few minutes. Leave the tab open.
              </Card>
            ) : result ? (
              <Card variant="panel" padding="lg" className="space-y-3">
                <article className="whitespace-pre-wrap text-sm leading-relaxed text-stone-200">
                  {result.brief}
                </article>
                <div className="flex flex-wrap items-center gap-3 border-t border-stone-800 pt-3 text-[11px] text-stone-500">
                  <span>{(result.durationMs / 1000).toFixed(1)}s</span>
                  <span>~${result.costUsd.toFixed(3)} (verify before billing)</span>
                  {result.toolsUsed.length > 0 ? (
                    <span>tools: {result.toolsUsed.map((t) => `${t.server}.${t.tool}`).join(', ')}</span>
                  ) : (
                    <span>no tools called</span>
                  )}
                  {result.entryId ? <span className="text-emerald-500">saved to matter knowledge</span> : null}
                </div>
              </Card>
            ) : (
              <Card variant="default" padding="lg" className="text-sm text-stone-500">
                Generate a brief the night before your hearing. It gives you the one thing to land,
                your strongest arguments with the evidence behind them, what the other side will
                argue, the documents to have open, your opening words, and the risks.
              </Card>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
